/**
 * Shared data for the AXAI-SEO-002 site pages (docs/specs/AXAI-SEO-002-site-build.md) and the
 * AXAI-SPEC-018 v0.2 pages (docs/specs/AXAI-SPEC-018-v0.2.md: /what-we-do, /ai-supported-work, /programs).
 *
 * SITE_PAGES is the register of every page added by that brief: path, title tag, meta
 * description, H1 and a one-line summary (used by the hub pages and "related pages" links).
 * NEW_SITE_ROUTES is the short form for the sitemap and footer.
 *
 * Copy rules (checked by tests/unit/site-pages.test.ts): never say how aXai produces its work,
 * no tool or vendor names, no invented numbers, clients, testimonials or outcomes. Prices come
 * only from src/data/video-offers.ts and CONSULTING_RATE; everything else is a fixed written quote.
 * Title tags carry no site suffix (the root layout adds it).
 */

import type { Metadata } from 'next';
import { OG_IMAGE } from '@/lib/brand/og';
import type { EnquiryType } from '@/data/enquiry-types';
import { CONSULTING_RATE, NDIS_REGISTRATION_STATUS } from '@/data/mentoring';
import { VIDEO_OFFERS, type VideoOffer } from '@/data/video-offers';

export const SITE_URL = 'https://axai.com.au';

export type SiteRouteGroup = 'services' | 'industries' | 'use-cases' | 'company' | 'what-we-do' | 'programs';

export interface SitePageEntry {
  path: string;
  /** Short name for menus, breadcrumbs and link text. */
  label: string;
  group: SiteRouteGroup;
  /** Title tag, without the site suffix. */
  title: string;
  /** Meta description, 155 characters or fewer. */
  description: string;
  h1: string;
  /** One sentence for hub cards and related links. */
  summary: string;
  /** Breadcrumb parent when it is not simply the folder above. */
  parent?: string;
}

// ---------------------------------------------------------------------------
// Prices (one source each)
// ---------------------------------------------------------------------------

const dollars = (price: string) => Number(price.replace(/[^0-9.]/g, ''));

/** Lowest listed video price. */
export const VIDEO_FROM_PRICE = VIDEO_OFFERS.filter((o) => o.price.startsWith('$'))
  .map((o) => o.price)
  .sort((a, b) => dollars(a) - dollars(b))[0];

/** A video offer by its name in src/data/video-offers.ts (throws if it is renamed or removed). */
export function videoOffer(name: string): VideoOffer {
  const offer = VIDEO_OFFERS.find((o) => o.name === name);
  if (!offer) throw new Error(`No video offer called "${name}"`);
  return offer;
}

/** The training video offer (up to 8 minutes): the price quoted wherever induction or training videos are priced. */
export const TRAINING_VIDEO = videoOffer('Training video');

/** The video price list as table rows: name, price, what it is for (price second so it is visible on a phone). */
export const VIDEO_PRICE_ROWS: string[][] = VIDEO_OFFERS.map((o) => [o.name, o.price, o.scope]);

export const QUOTE_WORDING = 'a fixed written quote, agreed before any work starts';

// ---------------------------------------------------------------------------
// The jobs message (every page carries it)
// ---------------------------------------------------------------------------

/**
 * THE aXai message (Annette approved this exact wording, 2 Oct 2026: "that message needs to be
 * repeated everywhere and pushed out there"). Every page's "Why aXai" section, the homepage band,
 * the pipeline's post boilerplate and social/YouTube descriptions use WHY_AXAI / WHY_AXAI_SHORT.
 * Do not paraphrase it on public pages; change it here only on Annette's instruction.
 */
export const WHY_AXAI = [
  // 18 Oct 2026: re-pointed at the routes of the redesigned site. /ai-supported-work and the
  // /innovations pages are 308s now, so the links went to [what we do] and the Lab's two pages.
  // 22 Oct 2026: /ai-supported-work is a page again (Annette: "ye we need an ai supported work
  // page"), so the phrase links its own page once more - K19 (i), every mention links.
  // Annette, 21 Oct 2026: "it is just axel and i at the moment - it should be more along
  // the lines of this is our plans and we are building the pipeline". The previous wording
  // was literally true of two people but its plural present tense implied a workforce and
  // an employment programme that does not exist yet. K19(h) already required future things
  // to read as planned; this brings the core message into line with that rule.
  // 24 Oct 2026 (AXAI-SITE-010 Part C): on the AXAI-BRAND-002 v0.1 section 3 message, which
  // WHY_AXAI_SHORT below already carries character for character. The old framing put AI as
  // support for people who need it; the message is that the barrier was the glue work, never
  // the person. Annette's 21 October clause is kept word for word in substance - the Part C
  // plan review flagged that an earlier draft of this rewrite dropped it, and she wrote it
  // three days ago in answer to a finding that the site over-promised paid work.
  'aXai is a disability-led studio that turns ideas into software and content with AI. AI takes the glue work; people keep deciding what is worth doing, and making it. That is what [AI-supported work](/ai-supported-work) sets out. Today the two founders do that work; we are building the pipeline so more disabled people can, in properly paid roles.',
  'Every project runs on our own infrastructure, the [Decision Bus](/decision-bus) and [Project Layer](/project-layer), so each step is planned, signed off by a person and on the record. That is our operating advantage.',
] as const;

/** Strip [label](/path) link markup (the same rule as src/components/site-pages/Inline.tsx). */
const stripLinks = (text: string) => text.replace(/\[([^\]]+)\]\([^)\s]+\)/g, (_, label: string) => label);

/**
 * WHY_AXAI as plain text (no link markup): what a visitor reads, and what prompts, structured
 * data and tests compare against. WHY_AXAI[0] links AI-supported work and WHY_AXAI[1] links the
 * Decision Bus and Project Layer (every mention of a page links to it); render both through Inline.
 */
export const WHY_AXAI_PLAIN = WHY_AXAI.map(stripLinks) as [string, string];

/** One-sentence form for footers, captions and post boilerplate. Plain text, no link markup. */
export const WHY_AXAI_SHORT =
  // AXAI-BRAND-002 v0.1 §3, character for character (AXAI-SITE-010). The older framing
  // put AI as support for people who need it; the message is that the barrier was the glue
  // work, never the person.
  'aXai is a disability-led studio that turns ideas into software and content with AI. AI takes the glue work — the chasing, the forms, the fourteenth retelling. People keep the part that was always theirs: deciding what is worth doing, and making it. New ideas. A different way of working.';

/**
 * WHY_AXAI_SHORT in Easy Read (5 Oct 2026): the same meaning, one idea per line. The pipeline adds
 * it (with a link to /ai-supported-work) to the end of every aXai post's Easy Read version, in
 * place of anything the model wrote about aXai. Change it only together with WHY_AXAI_SHORT.
 */
export const WHY_AXAI_EASY_READ: readonly string[] = [
  // AXAI-BRAND-002 v0.1 §3, character for character: one idea per line.
  // 24 Oct 2026 (Part 3 close-out): the owner reversed the word. "Neurodivergent-Led Innovation"
  // is a programme name and keeps it; the studio is disability-led.
  'aXai is a studio led by people with disability.',
  'AI does the chasing and the forms.',
  'People decide what is worth doing.',
  'People make it.',
  'That is a different way of working.',
];

/** @deprecated kept for older imports; identical to WHY_AXAI[0]. */
export const JOBS_MESSAGE = WHY_AXAI[0];

/**
 * SPEC-018 v0.2 pages use this name for the first "Why aXai" paragraph; identical to WHY_AXAI[0].
 */
export const INCLUSIVE_WORK_MESSAGE = WHY_AXAI[0];

// ---------------------------------------------------------------------------
// Page register
// ---------------------------------------------------------------------------

/**
 * The register of the site's public pages. AXAI-SITE-001 v0.2 replaced 43 entries (services,
 * industries, ai-for, use-cases, what-we-do children, innovations) with the 16 pages below.
 *
 * It is no longer the sitemap's source — src/app/sitemap.ts lists the routes itself — but it is
 * still what src/lib/avatar/site-knowledge.ts builds the assistant's knowledge from, so a stale
 * entry here means the avatar recommends a page that 308s. Every title, description, h1 and
 * summary below is the artboard's own wording for that page.
 */
export const SITE_PAGES: SitePageEntry[] = [
  {
    path: '/what-we-do',
    label: 'What we do',
    group: 'what-we-do',
    title: 'What we do',
    description:
      'Software and content. Your idea goes in; it comes out built, checked at every step, with the record to prove it.',
    h1: 'Work with the studio',
    summary: 'Four kinds of work: software and AI systems, content and video, digital accessibility, and Vybrid sessions.',
  },
  {
    // Restored 22 Oct 2026 (Annette: "ye we need an ai supported work page - didnt realise it was
    // gone"). The same fault /ai-mentoring had on 20 Oct, and worse: the assistant builds its
    // knowledge from this register, and the post pipeline's standard closing links this page, so
    // while it was a 308 both were sending every reader to a redirect. h1 is the page's own h1.
    path: '/ai-supported-work',
    label: 'AI-supported work',
    group: 'company',
    title: 'AI-supported work',
    description:
      'How aXai delivers every piece of work: six steps, a signature from a person with disability at step four, and a record of who signed and when.',
    h1: 'What AI-supported work means',
    summary:
      'The six steps every piece of work goes through, and the signature at step four that a person has to give.',
  },
  {
    // Restored 23 Oct 2026. Annette: "i do a search on google - questions and answers opens our
    // contact page? ... they are all mismatched?????" Google was still serving the old titles of
    // pages the rebrand retired, so the result promised "Questions and Answers" and the click
    // delivered a contact form. These three are back under their own addresses; the entries below
    // are the ones 092c8f6 deleted, word for word.
    path: '/faq',
    label: 'Questions and answers',
    group: 'company',
    title: 'Questions and Answers',
    description:
      'Plain answers about aXai prices, quotes, NDIS registration status, privacy and social procurement, for people deciding whether to buy from us.',
    h1: 'Questions buyers ask',
    summary: 'Plain answers about prices, quotes, NDIS status, privacy and procurement.',
  },
  {
    path: '/what-we-do/ai-for-ndis-providers',
    label: 'AI for NDIS and disability providers',
    group: 'what-we-do',
    title: 'AI for NDIS & Disability Providers',
    description:
      'AI for NDIS and disability providers: accessible participant information, induction content, admin automation, note drafting and staff knowledge bases.',
    h1: 'AI for NDIS and disability providers',
    summary:
      'Accessible participant information, induction content, admin automation, note drafting and staff knowledge bases, with a person signing off.',
  },
  {
    path: '/services/ai-consulting-small-business',
    label: 'AI consulting for small business',
    group: 'services',
    title: 'AI Consulting & Software for Small Business',
    description: `AI advice and custom software for Australian small businesses. Consulting at ${CONSULTING_RATE}; builds on a fixed written quote.`,
    h1: 'AI consulting and software, built alongside your team',
    summary: 'Plain-language AI advice, an AI policy for your staff, and software built with your team.',
  },
  {
    // Restored 20 Oct 2026 (Annette: "yes we need that page back"). The assistant builds its
    // knowledge from this register, so while the page was a 308 the assistant was recommending a
    // redirect. h1 below is the page's own h1, word for word.
    path: '/ai-mentoring',
    label: 'AI mentoring',
    group: 'programs',
    title: 'AI mentoring',
    description:
      'A goal-led mentoring program where people with disability learn to use AI tools for their own tasks, and stay in control. Register your interest.',
    h1: 'Learn to use AI for the things that matter to you',
    summary:
      'An upcoming program: people with disability learn to use AI tools for their own goals, with a mentor, at their own pace.',
  },
  {
    path: '/videos',
    label: 'aXai Videos',
    group: 'services',
    title: 'aXai Videos',
    description:
      'We build an avatar of you or someone on your team, put it in your brand, and have it say your words exactly as you wrote them.',
    h1: 'Your people, presenting your videos',
    // The page keeps the artboard's "A sample within a day"; the register does not, because
    // this summary is quoted by the avatar and by related-page cards, where it would read as a
    // standing promise rather than what that page offers (the claims rule, no turnaround promise).
    summary: `Induction, training, explainers and social video, presented by your own people or by Axel AI Assisted. From ${VIDEO_FROM_PRICE} inc GST.`,
  },
  {
    path: '/dsx',
    label: 'DSX',
    group: 'company',
    title: 'DSX',
    description:
      'Disability Services Exchange. Accessible compliance, policy and a collective voice for small and medium NDIS providers.',
    h1: 'Disability Services Exchange',
    summary: 'Co-founded by Annette Andersen and Brendon Grail. Recruiting founding members.',
  },
  {
    path: '/axread',
    label: 'aXread',
    group: 'services',
    title: 'aXread',
    description:
      'aXread turns a page of a website into its Easy Read version when a reader asks for it. Written in advance and checked by people.',
    h1: 'Easy Read for any page, on demand',
    summary: 'An Easy Read mode for your website. Written in advance and checked by people, so nothing runs at the moment of reading.',
  },
  {
    path: '/avatar',
    label: 'Avatar assistant',
    group: 'services',
    title: 'Avatar assistant',
    description:
      'An assistant for your website that answers only from your own pages. Text first, video on demand, and a real hand-off to a person.',
    h1: 'An assistant for your website that answers only from your own pages',
    summary: 'The assistant that runs Axel AI Assisted on this site, in your organisation\u2019s voice, with an Easy Read mode.',
  },
  {
    path: '/lab',
    label: 'Lab',
    group: 'company',
    title: 'Lab',
    description:
      'Everything we have built, with its status. The first two are the pipeline itself; they run underneath every job.',
    h1: 'Everything we have built, with its status',
    summary:
      'The Decision Bus, the Project Layer, aXstudio, the avatar assistant, Savanax, AxelV1, Context Scanner and PSight. aXstudio is in alpha: one accessible interface for every aXai product, where you order, review, approve and pay, one question at a time, built for people whose disability includes cognitive impairment. It is listed on this page and on the home page but does not have a page of its own yet, and ordering moves into it when it opens.',
  },
  {
    path: '/decision-bus',
    label: 'Decision Bus',
    group: 'company',
    title: 'Decision Bus',
    description:
      'The Decision Bus sits between the AI agents that build aXai’s work, including the coding agents, and anything they try to do.',
    h1: 'Every AI action, checked before it happens',
    summary: 'Intercepts every action, reviews the approach, holds the never list, fails closed, and keeps the record.',
  },
  {
    path: '/project-layer',
    label: 'Project Layer',
    group: 'company',
    title: 'Project Layer',
    description:
      'The Project Layer plans the steps of a job before anything runs and keeps that plan on the record.',
    h1: 'The plan, kept beside the record',
    summary: 'Plans first, records the difference when work departs from the plan, and feeds aXstudio.',
  },
  {
    path: '/axel-ai-assisted',
    label: 'Axel AI Assisted',
    group: 'company',
    title: 'Axel AI Assisted',
    description:
      'Axel AI Assisted was Annette\u2019s idea: an AI presenter and assistant, built here with AI, in Axel Andersen\u2019s likeness.',
    h1: 'Not Axel. An AI in his likeness.',
    summary: 'The person and the AI: Axel Andersen the person, and Axel AI Assisted, the AI built in his likeness that acts.',
  },
  {
    path: '/annette',
    label: 'Annette Andersen',
    group: 'company',
    title: 'Annette Andersen',
    description:
      'I sign the contracts, carry the liability and answer for the work. If something goes wrong, it comes to me.',
    h1: 'I lead aXai and I answer for the work',
    summary: `Director. Two ways to buy some time: an ideas day, or a Vybrid session at ${CONSULTING_RATE}.`,
  },
  {
    path: '/contact',
    label: 'Contact',
    group: 'company',
    title: 'Contact',
    description:
      'Email, and Annette replies within 24 hours. If something on this site does not work for you, say so first.',
    h1: 'No forms. A person.',
    summary: 'No contact forms. Email annette@axai.com.au and a person replies within 24 hours.',
  },
  {
    path: '/verified',
    // The crumb and the related-page cards use this; the page's own title and H1 carry the full
    // sentence. It read "Home · What “Verified by aXai” means" - a crumb that is a sentence, where
    // every other crumb on the site is two or three words (Annette, 21 Oct 2026). This is also
    // what LEGACY_BREADCRUMB_LABELS already had for /verified, so the two now agree.
    label: 'Verified by aXai',
    group: 'company',
    title: 'What “Verified by aXai” means',
    description:
      'When an item carries this mark, it passed every check, a person with disability reviewed it, and the record shows who and when.',
    h1: 'What the mark means',
    summary: 'Fixed checks first, a vision pass, a keyboard and screen-reader walkthrough, then a final check and a person\u2019s signature.',
  },
  {
    path: '/social-procurement',
    label: 'Social procurement',
    group: 'company',
    title: 'Social Procurement Supplier Australia',
    description:
      'An Australian social procurement supplier: accessible content and software made by disabled people — the two founders today, with the pipeline being built.',
    h1: 'A social procurement supplier you can buy from',
    summary: 'How buying from aXai counts toward social procurement commitments.',
  },
  {
    path: '/accessibility',
    label: 'Accessibility statement',
    group: 'company',
    title: 'Accessibility statement',
    description:
      'aXai builds to WCAG 2.2 AA. Every release is tested automatically for keyboard access, screen readers, colour contrast and forms.',
    h1: 'Accessibility statement',
    summary: 'What we build to, how it is tested, and who to tell when something does not work.',
  },
];

export const NEW_SITE_ROUTES: { path: string; label: string; group: SiteRouteGroup }[] = SITE_PAGES.map(
  ({ path, label, group }) => ({ path, label, group })
);

export function sitePage(path: string): SitePageEntry {
  const entry = SITE_PAGES.find((p) => p.path === path);
  if (!entry) throw new Error(`No site page registered at ${path}`);
  return entry;
}

/** Child pages of a hub (e.g. '/ai-for'), in register order. */
export function childPages(hub: string): SitePageEntry[] {
  return SITE_PAGES.filter((p) => p.path.startsWith(`${hub}/`));
}

/** Next.js metadata for a registered page: title (no suffix), description, canonical, Open Graph. */
export function siteMetadata(path: string): Metadata {
  const page = sitePage(path);
  return {
    title: page.title,
    description: page.description,
    alternates: { canonical: page.path },
    // OG_IMAGE is required: an openGraph block REPLACES the inherited file-convention image,
    // so leaving it out ships the page with no share card at all (AXAI-SEO-001).
    openGraph: {
      title: `${page.title} | aXai`,
      description: page.description,
      url: page.path,
      images: [OG_IMAGE],
    },
  };
}

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

/**
 * Pages outside the register that carry breadcrumbs (blind review, Oct 2026, item 19), and the
 * existing pages that are parents of register pages. Rendered by
 * src/components/site-pages/PageHero.tsx (the shared page top); the label is the breadcrumb text.
 */
export const LEGACY_BREADCRUMB_LABELS: Record<string, string> = {
  // AXAI-SITE-001 v0.2: one flat level of public pages, so these are the labels for the routes
  // that are not in the register (the register supplies its own).
  // The footer link says "Insights" (it said "Archive" for one day, 19-20 Oct 2026); the
  // breadcrumb keeps the page's own name. /journal is gone - it 308s to disabilityx.com.au,
  // which publishes it.
  '/insights': 'Insights',
  '/privacy': 'Privacy policy',
  '/terms': 'Terms and conditions',
  // Annette, 20 Oct 2026: "i think there is supposed to be breadcrumbs on every page". A page
  // can only have a trail if something knows what to call it, so every public page is named
  // here. A path with no label renders no trail rather than throwing (see breadcrumbTrail).
  '/what-we-do': 'What we do',
  '/ai-mentoring': 'AI mentoring',
  '/videos': 'AI avatar videos',
  '/dsx': 'DSX',
  '/axread': 'aXread',
  '/avatar': 'Avatar assistant',
  '/axel-ai-assisted': 'Axel AI Assisted',
  '/annette': 'Annette Andersen',
  '/contact': 'Contact',
  '/verified': 'Verified by aXai',
  '/social-procurement': 'Social procurement',
  '/accessibility': 'Accessibility',
  '/lab': 'Lab',
  // The Lab's products. Each sits under the Lab, which is how a visitor reaches it and what the
  // page's own eyebrow says ("Lab · Trading").
  '/decision-bus': 'Decision Bus',
  '/project-layer': 'Project Layer',
  '/savanax': 'Savanax',
  '/context-scanner': 'Context Scanner',
  '/document-layer': 'Document Layer',
  '/psight': 'PSight',
  '/axelv1': 'AxelV1',
  '/ersher': 'Ersher',
  '/mcp-connectivity': 'MCP connectivity',
  '/trading': 'Trading',
  '/moontrade': 'Moontrade',
  '/en-AU/privacy': 'Privacy policy',
  '/en-AU/privacy/easy-read': 'Privacy policy in Easy Read',
  // Added with the page, 21 Oct 2026. Without a label breadcrumbTrail returns null and the page
  // ships with no trail at all - the WCAG 2.2 AAA 2.4.8 failure Annette reported on the Easy Read
  // view, reappearing on the page written to answer it.
  '/en-AU/terms/easy-read': 'Website terms in Easy Read',
  '/en-AU/terms': 'Terms and conditions',
};

/**
 * Where a flat route sits under another one. Only needed where the address does not say it:
 * /trading is a Lab product but its path has no /lab in it.
 */
export const LEGACY_BREADCRUMB_PARENTS: Record<string, string> = {
  '/decision-bus': '/lab',
  '/project-layer': '/lab',
  '/savanax': '/lab',
  '/context-scanner': '/lab',
  '/document-layer': '/lab',
  '/psight': '/lab',
  '/axelv1': '/lab',
  '/ersher': '/lab',
  '/mcp-connectivity': '/lab',
  '/trading': '/lab',
  '/moontrade': '/lab',
  '/avatar': '/lab',
  '/axread': '/lab',
};


/**
 * A register label as a breadcrumb name: the innovation case studies are labelled
 * "Innovation: <name>" in the register (footer and sitemap grouping), and the crumb under
 * "Innovations" is the bare name (blind review R6, item 8).
 */
const crumbName = (label: string) => label.replace(/^Innovation: /, '');

export interface Crumb {
  name: string;
  path: string;
}

/** Breadcrumbs for a register page or a page in LEGACY_BREADCRUMB_LABELS: Home, the parent, the page. */
export function breadcrumbsFor(path: string): Crumb[] {
  const page = SITE_PAGES.find((p) => p.path === path);
  const label = page?.label ?? LEGACY_BREADCRUMB_LABELS[path];
  if (!label) throw new Error(`No site page or legacy breadcrumb registered at ${path}`);
  const crumbs: Crumb[] = [{ name: 'Home', path: '/' }];
  const parentPath =
    page?.parent ?? LEGACY_BREADCRUMB_PARENTS[path] ?? path.slice(0, path.lastIndexOf('/'));
  if (parentPath) {
    const registered = SITE_PAGES.find((p) => p.path === parentPath);
    const name = registered?.label ?? LEGACY_BREADCRUMB_LABELS[parentPath];
    if (name) crumbs.push({ name: crumbName(name), path: parentPath });
  }
  crumbs.push({ name: crumbName(label), path });
  return crumbs;
}

/**
 * The trail for any path, or null when nothing knows what to call that page.
 *
 * breadcrumbsFor THROWS on an unregistered path, which is right where a page asks for its own
 * trail — a page with no name is a mistake to fix. It is wrong for the site chrome, which asks on
 * every route including the ones that are not public pages at all, and must render nothing rather
 * than crash the page. Added 20 Oct 2026 with breadcrumbs on every page.
 */
export function breadcrumbTrail(path: string): Crumb[] | null {
  // Written without a regex replacement on purpose: a "$1" anywhere in this file reads as a
  // price to the register's own no-prices check (tests/unit/site-pages.test.ts).
  const clean = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  if (clean === '/' || clean === '') return null;
  const named = SITE_PAGES.some((p) => p.path === clean) || clean in LEGACY_BREADCRUMB_LABELS;
  if (!named) return null;
  return breadcrumbsFor(clean);
}

// ---------------------------------------------------------------------------
// Page content shape (rendered by src/components/site-pages/SitePage.tsx)
// ---------------------------------------------------------------------------

export interface SiteCard {
  title: string;
  text: string;
  /** Internal path or full URL. */
  href?: string;
  linkLabel?: string;
}

/** A paragraph may contain links written as [label](/path). */
export type SiteBlock =
  | string
  | { list: string[] }
  | { cards: SiteCard[] }
  | { faqs: SiteFaq[] }
  | { table: { caption: string; head: string[]; rows: string[][] } };

export interface SiteSection {
  heading: string;
  blocks: SiteBlock[];
}

export interface SiteFaq {
  q: string;
  /** Plain text only (it is also published as FAQ structured data). */
  a: string;
}

export interface SiteLink {
  href: string;
  label: string;
  text: string;
}

export type SiteEnquiryType = EnquiryType;

export interface SitePageContent {
  path: string;
  eyebrow: string;
  lead: string[];
  sections: SiteSection[];
  /** Extra "Why aXai" sentences after the jobs message. */
  whyAxai?: string[];
  /** Replaces WHY_AXAI[0] as the first "Why aXai" paragraph (SPEC-018 pages use INCLUSIVE_WORK_MESSAGE). */
  whyAxaiLead?: string;
  faqs?: SiteFaq[];
  related: SiteLink[];
  cta: {
    heading: string;
    text: string;
    /** Show the enquiry form with this type selected; leave out to show a contact button. */
    form?: SiteEnquiryType;
    buttonLabel?: string;
  };
  /** Adds Service structured data. */
  service?: { name: string; description: string };
}

/** A related link to a registered page, using its label and summary. */
export function relatedPage(path: string): SiteLink {
  const page = sitePage(path);
  return { href: page.path, label: page.label, text: page.summary };
}

/** Existing pages that new pages link to. */
export const EXISTING_LINKS = {
  videos: {
    href: '/videos',
    label: 'AI avatar videos',
    text: `See prices and presenters. Videos from ${VIDEO_FROM_PRICE}.`,
  },
  mentoring: {
    href: '/what-we-do',
    label: 'AI mentoring',
    text: 'People with disability learn to use AI for their own goals, with a mentor.',
  },
  custom: {
    href: '/what-we-do',
    label: 'Custom accessible tools and software',
    text: 'AI tools and software designed around the people who will use them.',
  },
  avatar: {
    href: '/avatar',
    label: 'AI avatars',
    text: 'A friendly AI guide for your website that knows your organisation.',
  },
  coding: {
    href: '/what-we-do#vybrid',
    label: 'AI coding (Vybrid)',
    text: 'Software and AI systems built with you, by an experienced engineer.',
  },
  accessibility: {
    href: '/what-we-do',
    label: 'Accessibility',
    text: 'Websites and software that work for everyone.',
  },
  contact: {
    href: '/contact',
    label: 'Contact us',
    text: 'Ask a question or book a call.',
  },
  architecture: {
    href: '/what-we-do',
    label: 'AI architecture',
    text: 'How we design AI systems that fit the way your organisation works.',
  },
  data: {
    href: '/decision-bus',
    label: 'Decision Bus',
    text: 'Every AI action is checked before it happens, and the record cannot be edited after the fact.',
  },
  innovations: {
    href: '/lab',
    label: 'Innovations',
    text: 'The products we have built: evidence of what we can do for you.',
  },
} satisfies Record<string, SiteLink>;
